import React, {useMemo, useState} from "react";
import {Box, Button, Chip, Dialog, IconButton, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {Rider} from "../../data/sheet";
import {colors, displayFont} from "../../styling/GlobalTheme";
import Reveal from "./Reveal";

const fisLink = (code: string) => code.startsWith("http") ? code
    : `https://www.fis-ski.com/DB/telemark/biographies.html?sectorcode=TM&fiscode=${encodeURIComponent(code)}&search=true`;
const instaLink = (v: string) => v.startsWith("http") ? v : `https://instagram.com/${v.replace("@", "")}`;
const initials = (r: Rider) => (r.firstName[0] || "") + (r.lastName[0] || "");

export const STAT_LABELS: [keyof Rider, string][] = [
    ["juniorMedals", "Médailles Juniors"], ["worldMedals", "Médailles Mondiaux"], ["wcWins", "Victoires CM"], ["globes", "Globes"],
];

function RiderCard({r, onClick, index}: { r: Rider; onClick: () => void; index: number }) {
    const [broken, setBroken] = useState(false);
    const wins = Number(r.wcWins) || 0;
    return (
        <Reveal delay={Math.min(index, 8) * 60}>
            <Box component="button" onClick={onClick} aria-label={`${r.firstName} ${r.lastName}`} sx={{
                position: "relative", display: "block", width: "100%", aspectRatio: "3 / 4", border: 0, p: 0,
                borderRadius: 4, overflow: "hidden", cursor: "pointer", bgcolor: colors.night2, color: "#fff", textAlign: "left",
                font: "inherit", transition: "transform .35s", "&:hover": {transform: "translateY(-6px)"},
                "&:hover img": {transform: "scale(1.07)"}, "&:focus-visible": {outline: `3px solid ${colors.glacier}`, outlineOffset: 3},
            }}>
                {r.photo && !broken
                    ? <img src={r.photo} alt="" loading="lazy" onError={() => setBroken(true)}
                           style={{position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform .6s"}}/>
                    : <Box sx={{position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: displayFont, fontSize: "5rem", color: "#33415c", background: `linear-gradient(135deg, #1d2b47, ${colors.night})`}}>{initials(r)}</Box>}
                <Box sx={{position: "absolute", inset: "auto 0 0", p: 2.2, pt: 8, background: "linear-gradient(transparent, rgba(0,0,0,.85))"}}>
                    {r.category && <Chip size="small" label={r.category} sx={{bgcolor: colors.glacier, color: "#000", fontWeight: 700, mb: 1}}/>}
                    <Typography sx={{fontFamily: displayFont, fontSize: "1.9rem", lineHeight: 1, letterSpacing: 1}}>
                        {r.firstName}<br/>{r.lastName}
                    </Typography>
                    <Typography sx={{fontSize: ".85rem", color: "#c9d2e0", mt: .5}}>
                        {wins > 0 ? `${wins} victoires en Coupe du monde` : [r.year, r.club].filter(Boolean).join(" · ")}
                    </Typography>
                </Box>
            </Box>
        </Reveal>
    );
}

function RiderDialog({r, onClose}: { r: Rider | null; onClose: () => void }) {
    if (!r) return null;
    const stats = STAT_LABELS.filter(([k]) => r[k] && r[k] !== "0");
    const info = [["Catégorie", r.category], ["Année", r.year], ["Club", r.club]].filter(x => x[1]);
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: 5, overflow: "hidden"}}}>
            <IconButton onClick={onClose} aria-label="Fermer" sx={{position: "absolute", top: 10, right: 10, bgcolor: "rgba(0,0,0,.5)", color: "#fff", "&:hover": {bgcolor: "#000"}}}><CloseIcon/></IconButton>
            {r.photo && <img src={r.photo} alt={`${r.firstName} ${r.lastName}`} style={{width: "100%", maxHeight: 380, objectFit: "cover", display: "block"}}/>}
            <Box sx={{p: 3}}>
                <Typography variant="h3" sx={{fontSize: "2.6rem", lineHeight: 1}}>{r.firstName} {r.lastName}</Typography>
                {r.bio && <Typography sx={{color: "text.secondary", mt: 1}}>{r.bio}</Typography>}
                {stats.length > 0 && (
                    <Box sx={{display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 1, mt: 2.5}}>
                        {stats.map(([k, label]) => (
                            <Box key={k} sx={{bgcolor: colors.snow, borderRadius: 3, p: 1.2, textAlign: "center"}}>
                                <Typography sx={{fontFamily: displayFont, fontSize: "2.2rem", lineHeight: 1, color: colors.glacierDark}}>{r[k] as string}</Typography>
                                <Typography sx={{fontSize: ".7rem", textTransform: "uppercase", color: "text.secondary"}}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {info.length > 0 && (
                    <Box component="dl" sx={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", mt: 2, mb: 0}}>
                        {info.map(([k, v]) => <React.Fragment key={k}><Box component="dt" sx={{color: "text.secondary", fontWeight: 600}}>{k}</Box><Box component="dd" sx={{m: 0}}>{v}</Box></React.Fragment>)}
                    </Box>
                )}
                {r.results.length > 0 && <>
                    <Typography sx={{mt: 2, fontWeight: 800, fontSize: ".8rem", textTransform: "uppercase", letterSpacing: 1, color: colors.glacierDark}}>Résultats</Typography>
                    <Box component="ul" sx={{mt: .5, pl: 2.5}}>{r.results.map((s, i) => <li key={i}>{s}</li>)}</Box>
                </>}
                <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", mt: 2}}>
                    {r.fisCode && <Button variant="contained" href={fisLink(r.fisCode)} target="_blank" rel="noopener">Profil FIS ↗</Button>}
                    {r.instagram && <Button variant="outlined" color="inherit" href={instaLink(r.instagram)} target="_blank" rel="noopener">Instagram ↗</Button>}
                </Box>
            </Box>
        </Dialog>
    );
}

export default function RiderGrid({riders, filters = true, limit}: { riders: Rider[]; filters?: boolean; limit?: number }) {
    const [cat, setCat] = useState("");
    const [open, setOpen] = useState<Rider | null>(null);
    const cats = useMemo(() => Array.from(new Set(riders.map(r => r.category).filter(Boolean))), [riders]);
    const list = riders.filter(r => !cat || r.category === cat).slice(0, limit);
    return (
        <>
            {filters && cats.length > 1 && (
                <Box sx={{display: "flex", gap: 1, flexWrap: "wrap", mb: 3}}>
                    {["", ...cats].map(c => (
                        <Chip key={c || "all"} label={c || "Tous"} onClick={() => setCat(c)} clickable
                              sx={{fontWeight: 700, px: 1, bgcolor: cat === c ? colors.glacier : "transparent", color: cat === c ? "#000" : "inherit",
                                  border: `1px solid ${cat === c ? colors.glacier : "rgba(128,140,160,.5)"}`}}/>
                    ))}
                </Box>
            )}
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "repeat(2, 1fr)", sm: "repeat(auto-fill, minmax(230px, 1fr))"}, gap: {xs: 1.5, sm: 2.5}}}>
                {list.map((r, i) => <RiderCard key={`${r.firstName}-${r.lastName}`} r={r} index={i} onClick={() => setOpen(r)}/>)}
            </Box>
            <RiderDialog r={open} onClose={() => setOpen(null)}/>
        </>
    );
}
